import axios from "axios";
import CONF from "@/Config.js";
import router from "@/router";
import {useLoading} from 'vue-loading-overlay';

const $loading = useLoading({
    // options
});

/**
 * initial state
 * 사용 예) this.$store.state.auth.is_login
 */
const state = {
  access_token: "", // 토큰 정보
  user_info: null, // 로그인 사용자 정보
  role_info: null, // 역할 정보 { role_code, role_name } - 표시용으로만 유지
  is_login: false, // 로그인 여부
  security_agreed: false // 보안 동의서 동의 여부 (백엔드 기록 기반)
  // 권한 체크 관련 상태 제거됨
};

/**
 * getters
 * 사용 예) this.$store.getters['auth/getUserInfo'];
 */
const getters = {
  getAccessToken: (state) => state.access_token,
  getIsLogin: (state) => state.is_login,
  getUserInfo: (state) => state.user_info,
  getRoleInfo: (state) => state.role_info,
  getRoleCode: (state) => state.role_info?.role_code,
  getRoleName: (state) => state.role_info?.role_name,
  getSecurityAgreed: (state) => state.security_agreed
  // 권한 체크 관련 getters 모두 제거됨
};

/**
 * mutations
 * 사용 예) this.$store.commit('auth/logout','');
 */
const mutations = {
  login(state, payload) {
    state.access_token = payload.token;
    state.user_info = payload.user_info;
    state.is_login = true;
  },
  logout(state) {
    state.access_token = "";
    state.user_info = null;
    state.role_info = null;
    state.is_login = false;
    state.security_agreed = false;
  },
  refreshToken(state, payload) {
    state.access_token = payload;
  },
  setRoleInfo(state, payload) {
    state.role_info = payload;
  },
  setSecurityAgreed(state, payload) {
    state.security_agreed = payload;
  }
  // 권한 관련 mutations 제거됨
};

/**
 * actions
 * 사용 예) this.$store.dispatch('auth/logout');
 */
const actions = {
  // 로그인 -> 토큰 반환
  login({ dispatch }, { email, password }) {
    const loader = $loading.show();

    axios
      .post(`${CONF.API_URL}/auth/user/token`, {
        userEmail: email,
        password,
      })
      .then((res) => {
        if (res.status === 200) {
          loader.hide();

          const loadDt = new Date();
          const expires_in = res.data.expires_in;
          const expire_time = new Date(Date.parse(loadDt) + 1000 * expires_in);

          localStorage.setItem("access_token", res.data.access_token);
          localStorage.setItem("expire_time", expire_time.toISOString());

          dispatch("getMemberInfo");
        }
      })
      .catch((err) => {
        alert("로그인 실패"); // 직접 메시지 추가
        loader.hide();
        console.error(err);
      });
  },

  // 사용자 정보 가져오기 (Promise 반환 추가)
  async getMemberInfo({ commit, dispatch }) {
    const token = localStorage.getItem("access_token");
    if (!token || token === "null") return;

    try {
      const response = await axios.get(`${CONF.API_URL}/auth/user/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // learner(미승인) 계정도 로그인 허용 — 강사 권한 요청/상태 화면 접근용.
      // 접근 범위 제한은 main.js 전역 가드에서 처리한다.
      const user_info = {
        idx: response.data.userIdx,
        name: response.data.userName,
        email: response.data.userEmail,
        userType: response.data.userType
      };

      // 역할 정보 저장 (백엔드에서 role_code, role_name 제공 시) - 표시용으로만 사용
      if (response.data.role_code && response.data.role_name) {
        commit("setRoleInfo", {
          role_code: response.data.role_code,
          role_name: response.data.role_name,
        });
      }

      // 보안 동의서 동의 여부 — 백엔드 응답 기반.
      // securityAgreement.agreed(객체) 또는 securityAgreedAt(ISOString)/securityAgreed(boolean) 모두 지원.
      const agreement = response.data.securityAgreement;
      const agreed =
        (agreement && agreement.agreed === true) ||
        !!response.data.securityAgreedAt ||
        response.data.securityAgreed === true;
      commit("setSecurityAgreed", agreed);

      commit("login", { token, user_info });

      // 기관 Master 여부 — GET /voucher/org/me (1건 이상이면 기관 관리 메뉴 표시, 0건·오류면 숨김).
      // users/me 에는 들어 있지 않아 별도 호출. 액션 안에서 오류를 삼키므로 로그인에는 영향 없음.
      await dispatch("organization/load", null, { root: true });

      // 권한 체크 제거됨 - 페이지 권한 로드하지 않음

      // 현재 경로 확인 후 리다이렉트 처리
      const currentRoute = router.currentRoute.value;
      const redirect = currentRoute.query.redirect;

      if (currentRoute.name === "Signin Cover") {
        if (redirect) {
          // learner 가 접근 불가한 경로면 전역 가드가 상태 화면으로 보정
          router.push(redirect);
        } else if (user_info.userType === "learner") {
          // 미승인 계정 → 강사 권한 요청 상태 화면으로
          router.push({ name: "InstructorAccessStatus" });
        } else {
          router.push({ name: "Sales" });
        }
      }
    } catch (err) {
      alert("사용자 정보를 가져올 수 없습니다.");
      router.push({ name: "Signin Cover" }); // 로그인 페이지로 이동
    }
  },

  // 권한 관련 actions 제거됨

  // 보안 동의서 동의 기록 (백엔드 영구 저장)
  // 동의 확인자 정보를 감사(audit) 목적으로 함께 전송:
  //   - userIdx / userEmail : 사용자 식별자 (계정 ID·이메일)
  //   - documentId / version : 동의한 문서 식별자·버전
  //   - method : 동의 방식 (체크박스 등)
  //   - agreed : 동의 여부 값
  //   - agreedAtClient : 클라이언트 측 타임스탬프 (참고용)
  // ※ IP 주소와 권위(authoritative) 타임스탬프는 위변조 방지를 위해 클라이언트 값을 신뢰하지 말고,
  //   백엔드가 요청 헤더(X-Forwarded-For / RemoteAddr)와 서버 시각에서 직접 기록해야 함.
  async agreeSecurityPledge({ commit, state }, payload = {}) {
    const token = localStorage.getItem("access_token");
    if (!token || token === "null") {
      throw new Error("인증 토큰이 없습니다.");
    }

    const user = state.user_info || {};
    const body = {
      version: payload.version || "1.0",
      documentId: payload.documentId || "security-pledge",
      agreed: payload.agreed !== false,
      method: payload.method || "checkbox",
      userIdx: user.idx,
      userEmail: user.email,
      agreedAtClient: payload.agreedAtClient || new Date().toISOString(),
    };

    await axios.post(
      `${CONF.API_URL}/auth/user/security-agreement`,
      body,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    commit("setSecurityAgreed", true);
  },

  refreshToken({ commit }) {
    const token = localStorage.getItem("access_token");

    axios
      .get(`${CONF.API_URL}/auth/user/refresh`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (res.status === 200) {
          const loadDt = new Date();
          const expires_in = res.data.expires_in;
          const expire_time = new Date(Date.parse(loadDt) + 1000 * expires_in);
          const refresh_token = res.data.access_token;

          localStorage.setItem("access_token", refresh_token);
          localStorage.setItem("expire_time", expire_time.toISOString());

          commit("refreshToken", refresh_token);
        }
      })
      .catch(() => {
        alert("토큰 갱신 실패");
      });
  },

  logout({ commit, dispatch }) {
    localStorage.removeItem("access_token");
    commit("logout");
    dispatch("organization/reset", null, { root: true });

    router.push({ name: "Signin Cover" });
  },

  /**
   * OAuth 로그인 (Google/Apple)
   * 자동 회원가입 포함
   */
  async oauthLogin({ dispatch }, { provider, idToken }) {
    const loader = $loading.show();

    try {
      // 사용자 언어 감지
      const userLanguage = navigator.language.startsWith('ko') ? 'ko' : 'en';

      // OAuth 로그인 API 호출 (자동 회원가입 처리)
      const res = await axios.post(`${CONF.API_URL}/auth/user/oauth/login`, {
        provider,
        idToken,
        firstLanguage: userLanguage
      });
      const response = res.data;

      // 토큰 저장
      const loadDt = new Date();
      const expires_in = response.expires_in || 3600;
      const expire_time = new Date(Date.parse(loadDt) + 1000 * expires_in);

      localStorage.setItem("access_token", response.access_token);
      localStorage.setItem("expire_time", expire_time.toISOString());

      loader.hide();

      // 일반 로그인과 동일하게 getMemberInfo를 통해 사용자 정보 조회
      dispatch("getMemberInfo");
    } catch (error) {
      loader.hide();

      if (error.response?.status === 400) {
        alert("잘못된 OAuth 제공자입니다.");
      } else if (error.response?.status === 401) {
        alert("OAuth 인증에 실패했습니다.");
      } else if (error.response?.status === 403) {
        alert("접근이 제한된 계정입니다. 고객센터에 문의해주세요.");
      } else {
        alert("로그인 중 오류가 발생했습니다.");
      }

      console.error("OAuth login error:", error);
      throw error;
    }
  },

  /**
   * OAuth 회원가입 (Google/Apple) — 강사 권한 요청 가입 플로우 전용
   * userType 은 반드시 learner (instructor 전송 시 400) — 강사 권한은 승인으로만 부여됨.
   * 성공 시 토큰 저장 + getMemberInfo 로 store 를 채운다.
   * (회원가입 화면에서 호출되므로 getMemberInfo 의 Signin Cover 리다이렉트는 발생하지 않음)
   */
  async oauthRegister({ dispatch }, { provider, idToken }) {
    const loader = $loading.show();

    try {
      const userLanguage = navigator.language.startsWith('ko') ? 'ko' : 'en';

      const res = await axios.post(`${CONF.API_URL}/auth/user/oauth/register`, {
        provider,
        idToken,
        userType: "learner",
        firstLanguage: userLanguage
      });
      const response = res.data;

      const loadDt = new Date();
      const expires_in = response.expires_in || 3600;
      const expire_time = new Date(Date.parse(loadDt) + 1000 * expires_in);

      localStorage.setItem("access_token", response.access_token);
      localStorage.setItem("expire_time", expire_time.toISOString());

      loader.hide();

      await dispatch("getMemberInfo");
    } catch (error) {
      loader.hide();
      console.error("OAuth register error:", error);
      throw error; // 에러별 안내는 호출한 화면에서 처리
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
